<template>
    <div class="form">
        <div class="form__section" v-if="!user">
            <div class="copy-light">
                Mithilfe von Listen können Sie Ihre Favoriten nach Themen zusammenfassen (z.B. "Italien-Reise" oder "Täglich unterwegs"). <br><br>Loggen Sie sich ein, um Listen erstellen zu können.
            </div>
            <a class="form__section__link" @click.prevent="openLoginMenuClickHandler" v-if="!user">Jetzt einloggen</a>
        </div>
        <div class="form__section" v-if="user">
            <div class="form__title">Nach Listen filtern</div>
            <div class="form__description copy-light">
                Mithilfe von Listen können Sie Ihre Favoriten nach Themen zusammenfassen (z.B. "Italien-Reise" oder "Täglich unterwegs"). Wählen Sie beim Speichern eines Ortes einfach eine Liste aus oder erstellen Sie eine neue.
            </div>

            <div class="form__fields">
                <div class="form__field-row">
                    <SmrChoiceGroup 
                        v-if="listOptions.length > 0"
                        :values="checkedLists" 
                        :options="listOptions"
                        :output-array="true"
                        :updatableValues="true"
                        :showInRows="true"
                        :minAmount="1"
                        @change="listsChangeHandler"
                        @editList="editListClickHandler"
                        @deleteList="deleteListClickHandler"
                    />

                    <div class="form__add-item" :class="{ 'move-up' : listOptions.length % 2 !== 0}" @click="newListClickHandler">
                        <span class="form__add-item__text">Neue Liste erstellen</span>
                        <Icon class="form__add-item__icon">
                            <IconPlusCircle />
                        </Icon>
                    </div>
                </div>
            </div>
        </div>
        <div class="form__section" v-if="user && favouriteCategories && favouriteCategories.length > 0">
            <div class="form__title">Nach Kategorien filtern</div>
            <div class="form__fields">
                <div class="form__field-row">
                    <SmrPills 
                        ref="categoryPills"
                        :values="checkedCategories" 
                        :options="categoriesOptions"
                        :minAmount="1"
                        @change="categoriesChangeHandler"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import eventBus from '@/utils/event-bus';
import settingsTabBlueprint from '@/mixins/settings-tab-blueprint';
import messageBoxBlueprint from '@/mixins/message-box-blueprint.js';
import SmrChoiceGroup from '@/components/form/SmrChoiceGroup.vue';
import SmrPills from '@/components/form/SmrPills.vue'
import Icon from '@/components/Icon.vue'
import IconPlusCircle from '@/components/icons/IconPlusCircle.vue'
import { trackEvent, trackGa4Event } from '@/utils/tracking'

export default {
    components: {
        SmrChoiceGroup,
        SmrPills,
        Icon,
        IconPlusCircle
    },
    mixins: [
        settingsTabBlueprint,
        messageBoxBlueprint
    ],
    data() {
        return {
            data: {
                uncheckedLists: [],
                uncheckedCategories: []
            },
            checkedLists: [],
            checkedCategories: [],
        }
    },
    computed: {
        ...mapGetters(['user', 'favouritesLists', 'favouriteCategories']),
        listOptions() {
            let options = [
                {
                    value: '', label: 'Gemerkte Orte',
                }
            ]
            this.favouritesLists.forEach(list => {
                options.push({
                    value: list.id,
                    label: list.name,
                    additionalActions: [
                        {
                            icon: 'IconEdit',
                            label: 'Liste bearbeiten',
                            event: 'editList'
                        },
                        {
                            icon: 'IconDelete',
                            label: 'Liste löschen',
                            event: 'deleteList'
                        },
                    ]
                })
            })
            return options
        },
        categoriesOptions() {
            return this.favouriteCategories
        }
    },
    mounted() {
        this.updateCheckedLists()
        this.updateCheckedCategories()
    },
    watch: {
        favouritesLists() {
            this.updateCheckedLists()
        },
        favouriteCategories() {
            this.updateCheckedCategories()
        }
    },
    methods: {
        openLoginMenuClickHandler() {
            eventBus.$emit('closeOptionsPanel');
            eventBus.$emit('showLoginMenu');
        },
        newListClickHandler() {
            trackEvent({ category: 'Favorites', action: 'Settings', label: 'Add New List' });
            trackGa4Event({ga4Parameter1Name: 'options', ga4Parameter1Value: 'new_list', ga4Event: 'favorites'})
            eventBus.$emit('showFavouritesListPanel')
        },
        editListClickHandler(listId) {
            const listData = this.favouritesLists.find(list => list.id === listId)
            eventBus.$emit('showFavouritesListPanel', listData)
        },
        deleteListClickHandler(listId) {
            this.showConfirmation(
                'Liste und enthaltene Favoriten wirklich löschen?',
                'Durch das Löschen der Liste werden auch alle darin abgespeicherten Orte unwiderruflich gelöscht. Möchten Sie die Liste wirklich löschen?',
                'Verstanden',
                'Abbrechen',
                () => { this.deleteList(listId) }
            );
        },
        listsChangeHandler(output) {
            this.listOptions.forEach(list => {
                if(!output.includes(list.value) && !this.data.uncheckedLists.includes(list.value)) {
                    this.data.uncheckedLists.push(list.value)
                } else if (output.includes(list.value) && this.data.uncheckedLists.includes(list.value)) {
                    const index = this.data.uncheckedLists.indexOf(list.value);
                    if (index !== -1) {
                        this.data.uncheckedLists.splice(index, 1);
                    }
                }
            })
        },
        categoriesChangeHandler(output) {
            this.categoriesOptions.forEach(category => {
                if(!output.includes(category.value) && !this.data.uncheckedCategories.includes(category.value)) {
                    this.data.uncheckedCategories.push(category.value)
                } else if (output.includes(category.value) && this.data.uncheckedCategories.includes(category.value)) {
                    const index = this.data.uncheckedCategories.indexOf(category.value);
                    if (index !== -1) {
                        this.data.uncheckedCategories.splice(index, 1);
                    }
                }
            })
        },
        deleteList(listId) {
            this.$store.dispatch('removeFavouritesList', listId)
        },
        updateCheckedLists() {
            let lists = []
            this.listOptions.forEach(list => {
                if(!this.data.uncheckedLists.includes(list.value)) {
                    lists.push(list.value)
                }
            })
            this.checkedLists = lists
        },
        updateCheckedCategories() {
            let categories = []
            this.categoriesOptions.forEach(category => {
                if(!this.data.uncheckedCategories.includes(category.value)) {
                    categories.push(category.value)
                }
            })
            this.checkedCategories = categories
            setTimeout(() => {
                this.$refs['categoryPills'].setInitialValues()
            }, 10);
        }
    }
}
</script>
